import {
  Card,
  CardContent,
  CardMedia,
  Chip,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Button,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Link } from 'gatsby';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import React, { useEffect, useMemo, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import Header from './Header';
import Footer from './Footer';
import Layout from './Layout';
import { colors } from './styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      backgroundColor: '#f8f9fa',
    },
    container: {
      maxWidth: 1280,
      marginLeft: 'auto',
      marginRight: 'auto',
      padding: theme.spacing(1),
    },
    pageHeader: {
      textAlign: 'center',
      marginBottom: theme.spacing(1),
    },
    stickyFilterContainer: {
      position: 'sticky',
      top: 80,
      zIndex: 1000,
      backgroundColor: '#f8f9fa',
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    filtersContainer: {
      padding: theme.spacing(3),
      backgroundColor: 'white',
      borderRadius: theme.shape.borderRadius * 2,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.06)',
      width: '100%',
    },
    filtersToggleButton: {
      marginBottom: theme.spacing(2),
      '&:focus': {
        outline: 'none',
      },
    },
    searchAndCoachContainer: {
      display: 'flex',
      gap: theme.spacing(2) + 'px',
      marginBottom: '20px',
      [theme.breakpoints.up('md')]: {
        flexDirection: 'column',
        gap: theme.spacing(1) + 'px',
      },
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        gap: theme.spacing(1) + 'px',
      },
    },
    searchContainer: {
      flex: 1,
      [theme.breakpoints.up('md')]: {
        flex: 'none',
      },
      [theme.breakpoints.down('xs')]: {
        flex: 'none',
      },
    },
    coachContainer: {
      flex: 1,
      [theme.breakpoints.up('md')]: {
        flex: 'none',
      },
      [theme.breakpoints.down('xs')]: {
        flex: 'none',
      },
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: 200,
    },
    sourceImage: {
      width: 30,
      height: 30,
      borderRadius: 15,
      marginRight: theme.spacing(2),
    },
    chip: {
      margin: 0,
      height: 32,
      fontSize: '0.875rem',
      fontWeight: 500,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      },
    },
    card: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform .2s ease-in-out, box-shadow .2s ease-in-out',
      borderRadius: theme.shape.borderRadius,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      '&:hover': {
        transform: 'scale(1.05)',
        boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
      },
    },
    cardMedia: {
      aspectRatio: '3',
      position: 'relative',
    },
    sourceImageOverlay: {
      position: 'absolute',
      top: 12,
      right: 12,
      width: 40,
      height: 40,
      borderRadius: '50%',
      border: '2px solid rgba(255, 255, 255, 0.9)',
      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(4px)',
      zIndex: 1,
      overflow: 'hidden',
    },
    sourceImageCircle: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    badgesContainer: {
      marginTop: 'auto',
      paddingTop: theme.spacing(1),
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing(1) + 'px',
    },
    badgeContainer: {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      display: 'flex',
      alignItems: 'center',
      fontSize: 12,
      height: 24,
      borderRadius: 12,
      color: 'white',
    },
    cardContent: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(2),
    },
    sectionHeader: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
      textAlign: 'center',
      width: '100%',
    },
    trackName: {
      fontWeight: 600,
      lineHeight: 1.3,
      flexGrow: 1,
      marginBottom: theme.spacing(1),
    },
    sourceName: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    content: {
      justifyContent: 'center',
    },
    mainContent: {
      display: 'flex',
      gap: theme.spacing(4) + 'px',
      alignItems: 'flex-start',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
      [theme.breakpoints.down('xs')]: {
        gap: '0px',
      },
    },
    sidebar: {
      width: 320,
      flexShrink: 0,
      position: 'sticky',
      top: 150,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        position: 'static',
        marginBottom: theme.spacing(3),
      },
    },
    tracksContainer: {
      flex: 1,
    },
    link: {
      display: 'flex',
      textDecoration: 'none',
      color: 'inherit',
      height: '100%',
    },
    emptyState: {
      textAlign: 'center',
      padding: theme.spacing(6),
      backgroundColor: 'white',
      borderRadius: theme.shape.borderRadius,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      marginTop: theme.spacing(3),
    },
    emptyStateIcon: {
      fontSize: 64,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
    },
  })
);

enum TrackType {
  Comp = 'competitions',
  Membership = 'memberships',
  Other = 'others',
  Plan = 'plans',
  Race = 'races',
}

const TrackMap = {
  [TrackType.Comp]: { color: colors.leaderboardColor, name: 'Competition' },
  [TrackType.Race]: { color: colors.raceColor, name: 'Live Race' },
  [TrackType.Membership]: {
    color: colors.membershipColor,
    name: 'Weekly Programming',
  },
  [TrackType.Plan]: { color: colors.planColor, name: 'Training Plan' },
  [TrackType.Other]: {
    color: colors.libraryColor,
    name: 'Library',
  },
};

const ErgTypeMap = {
  row: 'Row',
  ski: 'Ski',
  bike: 'Bike',
  'fan-bike': 'Fan Bike',
  run: 'Run',
  multi: 'MultiErg',
};

function maybeSortTracks(tracks: any[], trackType: TrackType) {
  if (
    tracks &&
    (trackType === TrackType.Comp || trackType === TrackType.Race)
  ) {
    return sortBy(tracks, (t) => t.compDate);
  } else {
    return tracks;
  }
}

function getTrackType(track: any): TrackType {
  if (track.planWeeks) return TrackType.Plan;
  if (
    track.activePrices.length &&
    track.activePrices.every((p) => p.recurringMonths)
  )
    return TrackType.Membership;
  if (track.compStatus) {
    if (track.features && track.features.includes('race')) {
      return TrackType.Race;
    }
    return TrackType.Comp;
  }
  return TrackType.Other;
}

const DiscoverPage = ({ pageContext }) => {
  const { sources } = pageContext;
  const [sourceSlug, setSourceSlug] = useQueryParam('source', StringParam);
  const [ergType, setErgType] = useQueryParam('erg', StringParam);
  const [trackTypeFilter, setTrackTypeFilter] = useQueryParam(
    'type',
    StringParam
  );
  const [searchQuery, setSearchQuery] = useQueryParam('search', StringParam);
  const [weeksFilter, setWeeksFilter] = useQueryParam('weeks', StringParam);
  const styles = useStyles();
  const [filteredTracks, setFilteredTracks] = useState(pageContext.tracks);
  const [selectedSource, setSelectedSource] = useState<any>();
  const [filtersVisible, setFiltersVisible] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  // This is a hack to ensure the component is mounted on the client side
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, [isClient]);

  // Get unique week values from training plans
  const uniqueWeeks = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...pageContext.tracks
              .filter((t) => t.planWeeks)
              .filter((t) => (sourceSlug ? t.source.slug === sourceSlug : true))
              .filter((t) => (ergType ? t.features.includes(ergType) : true))
              .map((t) => t.planWeeks),
            ...(weeksFilter ? [Number(weeksFilter)] : []),
          ].sort((a, b) => a - b)
        )
      ) as number[],
    [sourceSlug, ergType, weeksFilter]
  );

  useEffect(() => {
    if (sourceSlug) {
      const tracksForSource = pageContext.tracks.filter(
        (t) => t.source.slug === sourceSlug
      );
      if (tracksForSource.length === 0) {
        // If no tracks found for this source, clear the source filter
        setSourceSlug(undefined, 'replaceIn');
        return;
      }
    }

    const tracks = pageContext.tracks
      .filter((t) => (sourceSlug ? t.source.slug === sourceSlug : true))
      .filter((t) => (ergType ? t.features.includes(ergType) : true))
      .filter((t) => {
        if (!trackTypeFilter) return true;
        const trackType = getTrackType(t);
        return trackType === trackTypeFilter;
      })
      .filter((t) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          t.name.toLowerCase().includes(query) ||
          t.source.name.toLowerCase().includes(query) ||
          (t.description && t.description.toLowerCase().includes(query))
        );
      })
      .filter((t) => {
        if (!weeksFilter || trackTypeFilter !== 'plans') return true;
        return t.planWeeks && t.planWeeks.toString() === weeksFilter;
      });

    setFilteredTracks(tracks);
    setSelectedSource(sources.find((s) => s.slug === sourceSlug));
  }, [sourceSlug, ergType, trackTypeFilter, searchQuery, weeksFilter]);

  const groupedTracks = groupBy(filteredTracks, getTrackType);

  // Calculate number of applied filters
  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (sourceSlug) count++;
    if (ergType) count++;
    if (trackTypeFilter) count++;
    if (searchQuery) count++;
    if (weeksFilter) count++;
    return count;
  }, [sourceSlug, ergType, trackTypeFilter, searchQuery, weeksFilter]);

  const filtersContent = (
    <div className={styles.filtersContainer}>
      <Typography
        variant="h6"
        component="h2"
        style={{
          marginBottom: '16px',
          fontWeight: 600,
          color: '#333',
        }}
      >
        Filters
      </Typography>

      <div className={styles.searchAndCoachContainer}>
        <div className={styles.searchContainer}>
          <Typography
            variant="subtitle2"
            style={{
              marginBottom: '8px',
              fontWeight: 600,
              color: '#666',
            }}
          >
            Search
          </Typography>
          <TextField
            variant="outlined"
            size="medium"
            value={searchQuery || ''}
            onChange={(event) =>
              setSearchQuery(event.target.value || undefined, 'replaceIn')
            }
            fullWidth
            inputProps={{ style: { padding: 14 } }}
          />
        </div>

        <div className={styles.coachContainer}>
          <Typography
            variant="subtitle2"
            style={{
              marginBottom: '8px',
              fontWeight: 600,
              color: '#666',
            }}
          >
            Coach
          </Typography>
          <FormControl variant="outlined" size="small" fullWidth>
            <Select
              value={sourceSlug || 'All'}
              onChange={(event) => {
                const value = event.target.value as string;
                setSourceSlug(value === 'All' ? undefined : value);
              }}
            >
              <MenuItem value="All">
                <div
                  style={{
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  All
                </div>
              </MenuItem>
              {sources.map((source) => (
                <MenuItem key={source.slug} value={source.slug}>
                  <img className={styles.sourceImage} src={source.imageUrl} />
                  {source.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <Typography
          variant="subtitle2"
          style={{
            marginBottom: '8px',
            fontWeight: 600,
            color: '#666',
          }}
        >
          Erg
        </Typography>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {Object.entries(ErgTypeMap).map(([value, label]) => (
            <Chip
              key={value}
              className={styles.chip}
              label={label}
              color={isClient && ergType === value ? 'primary' : undefined}
              variant={isClient && ergType === value ? 'default' : 'outlined'}
              onClick={() =>
                setErgType((type) => (type === value ? undefined : value))
              }
            />
          ))}
        </div>
      </div>

      {trackTypeFilter === 'plans' && uniqueWeeks.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <Typography
            variant="subtitle2"
            style={{
              marginBottom: '8px',
              fontWeight: 600,
              color: '#666',
            }}
          >
            Number of Weeks
          </Typography>
          <FormControl variant="outlined" size="small" fullWidth>
            <Select
              value={weeksFilter || 'Any'}
              onChange={(event) => {
                const value = event.target.value as string;
                setWeeksFilter(value === 'Any' ? undefined : value);
              }}
            >
              <MenuItem value="Any">
                <div
                  style={{
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Any
                </div>
              </MenuItem>
              {uniqueWeeks.map((weeks: number) => (
                <MenuItem key={weeks} value={weeks.toString()}>
                  <div
                    style={{
                      height: 30,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {weeks} weeks
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}
    </div>
  );

  return (
    <Layout
      titlePrefixes={[
        selectedSource ? String(selectedSource.name) : 'Discover',
      ]}
    >
      <Header solid />
      <section className={styles.root}>
        <Box className={styles.container}>
          <div className={styles.pageHeader}>
            <Typography variant="h3" component="h1">
              What are you{' '}
              <span style={{ color: colors.secondaryColor }}>looking for?</span>
            </Typography>
          </div>

          <div className={styles.stickyFilterContainer}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                justifyContent: 'center',
                maxWidth: '800px',
                margin: '0 auto',
              }}
            >
              {Object.entries(TrackMap).map(([trackType, trackInfo]) => (
                <Chip
                  key={trackType}
                  className={styles.chip}
                  label={trackInfo.name}
                  style={{
                    backgroundColor:
                      isClient && trackTypeFilter === trackType
                        ? trackInfo.color
                        : undefined,
                    color:
                      isClient && trackTypeFilter === trackType
                        ? 'white'
                        : undefined,
                  }}
                  variant={
                    isClient && trackTypeFilter === trackType
                      ? 'default'
                      : 'outlined'
                  }
                  onClick={() =>
                    setTrackTypeFilter((type) =>
                      type === trackType ? undefined : trackType
                    )
                  }
                />
              ))}
            </div>
          </div>

          <div className={styles.mainContent}>
            <div className={styles.sidebar}>
              {isMobile && (
                <Button
                  variant="outlined"
                  onClick={() => setFiltersVisible(!filtersVisible)}
                  fullWidth
                  className={styles.filtersToggleButton}
                  style={{
                    borderColor: colors.primaryColor,
                    color: colors.primaryColor,
                    fontWeight: 600,
                    padding: '12px 16px',
                    borderRadius: '8px',
                  }}
                >
                  {`${filtersVisible ? 'Hide' : 'Show'} Filters${
                    appliedFiltersCount > 0 ? ` (${appliedFiltersCount})` : ''
                  }`}
                </Button>
              )}

              {isMobile ? (
                <Collapse in={filtersVisible} timeout="auto" unmountOnExit>
                  {filtersContent}
                </Collapse>
              ) : (
                filtersContent
              )}
            </div>

            <div className={styles.tracksContainer}>
              {filteredTracks.length === 0 ? (
                <Box className={styles.emptyState}>
                  <Typography variant="h4" component="h2" gutterBottom>
                    No results found
                  </Typography>
                  <Typography variant="body1" color="textSecondary" paragraph>
                    Try adjusting your filters to find more content
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    You can try searching with different terms, selecting
                    different coaches, equipment types, or categories
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2} style={{ alignItems: 'stretch' }}>
                  {Object.entries(TrackMap).flatMap(
                    ([trackType, trackInfo]) => {
                      const tracks = maybeSortTracks(
                        groupedTracks[trackType],
                        trackType as TrackType
                      );

                      if (!tracks) return [];

                      return tracks.map((track) => (
                        <Grid item xs={12} sm={6} md={6} lg={4} key={track.id}>
                          <Link
                            to={`/${track.webPath}`}
                            className={styles.link}
                          >
                            <Card className={styles.card}>
                              <CardMedia
                                className={styles.cardMedia}
                                image={track.featuredImage || track.imageUrl}
                                title={track.name}
                              >
                                <div className={styles.sourceImageOverlay}>
                                  <img
                                    className={styles.sourceImageCircle}
                                    src={track.source.imageUrl}
                                    alt={track.source.name}
                                  />
                                </div>
                              </CardMedia>
                              <CardContent className={styles.cardContent}>
                                <Typography
                                  className={styles.badgeContainer}
                                  style={{
                                    backgroundColor: trackInfo.color,
                                    color: 'white',
                                    marginBottom: '8px',
                                    alignSelf: 'flex-start',
                                  }}
                                >
                                  {trackInfo.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  component="p"
                                  className={styles.sourceName}
                                >
                                  {track.source.name}
                                </Typography>
                                <Typography
                                  variant="h6"
                                  component="h3"
                                  className={styles.trackName}
                                >
                                  {track.name}
                                </Typography>
                                <Box className={styles.badgesContainer}>
                                  <Typography
                                    className={styles.badgeContainer}
                                    style={{
                                      backgroundColor: 'transparent',
                                      border: `2px solid ${trackInfo.color}`,
                                      color: trackInfo.color,
                                    }}
                                  >
                                    {track.activePrice.displayText}
                                  </Typography>
                                  {!!track.compDateDisplay && (
                                    <Typography
                                      className={styles.badgeContainer}
                                      style={{
                                        backgroundColor: 'transparent',
                                        border: `2px solid ${trackInfo.color}`,
                                        color: trackInfo.color,
                                      }}
                                    >
                                      {track.compDateDisplay}
                                    </Typography>
                                  )}
                                  {(trackType === TrackType.Comp ||
                                    trackType === TrackType.Race) &&
                                    (track.usersCount ?? 0) > 1 && (
                                      <Typography
                                        className={styles.badgeContainer}
                                        style={{
                                          backgroundColor: 'transparent',
                                          border: `2px solid ${trackInfo.color}`,
                                          color: trackInfo.color,
                                        }}
                                      >
                                        {track.usersCount} athletes
                                      </Typography>
                                    )}
                                  {track.planWeeks && (
                                    <Typography
                                      className={styles.badgeContainer}
                                      style={{
                                        backgroundColor: 'transparent',
                                        border: `2px solid ${trackInfo.color}`,
                                        color: trackInfo.color,
                                      }}
                                    >
                                      {track.planWeeks} weeks
                                    </Typography>
                                  )}
                                  {track.features
                                    .filter((f) =>
                                      Object.keys(ErgTypeMap).includes(f)
                                    )
                                    .map((f) => (
                                      <Typography
                                        key={f}
                                        className={styles.badgeContainer}
                                        style={{
                                          backgroundColor: 'transparent',
                                          border: `2px solid ${trackInfo.color}`,
                                          color: trackInfo.color,
                                        }}
                                      >
                                        {ErgTypeMap[f]}
                                      </Typography>
                                    ))}
                                </Box>
                              </CardContent>
                            </Card>
                          </Link>
                        </Grid>
                      ));
                    }
                  )}
                </Grid>
              )}
            </div>
          </div>
        </Box>
      </section>
      <Footer />
    </Layout>
  );
};

export default DiscoverPage;
